import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Transaction } from '@/types';
import { Loader2, Mail, Send } from 'lucide-react';

interface EmailBillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: any;
  };
  billNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
}

export function EmailBillDialog({
  open,
  onOpenChange,
  transaction,
  client,
  billNumber,
  issueDate,
  companyName,
  companyLogo,
  bankDetails
}: EmailBillDialogProps) {
  const [emailTo, setEmailTo] = useState(client?.email || '');
  const [subject, setSubject] = useState(`Cobrança ${billNumber} - ${companyName}`);
  const [message, setMessage] = useState(
    `Prezado(a) ${client?.name || 'Cliente'},\n\nGostaríamos de informar que consta em nossos registros uma cobrança pendente.\n\nValor: R$ ${transaction.amount.toFixed(2).replace('.', ',')}\nData de Vencimento: ${transaction.due_date ? new Date(transaction.due_date).toLocaleDateString('pt-BR') : 'A definir'}\n\nSeguem os dados para pagamento:\n${bankDetails ? `- Banco: ${bankDetails.bank || 'Não informado'}\n- Agência: ${bankDetails.agency || 'Não informada'}\n- Conta: ${bankDetails.account || 'Não informada'}\n${bankDetails.pixKey ? `- PIX: ${bankDetails.pixKey}` : ''}` : 'Os dados para pagamento serão fornecidos mediante contato.'}\n\nPedimos a gentileza de regularizar o pagamento o mais breve possível.\n\nCaso já tenha realizado o pagamento, por favor, desconsidere este e-mail.\n\nPara qualquer dúvida, estamos à disposição.\n\nAtenciosamente,\n${companyName}`
  );
  const [isSending, setIsSending] = useState(false);
  const [sendWithPDF, setSendWithPDF] = useState(true);

  // Handle sending email
  const handleSendEmail = async () => {
    if (!emailTo) {
      alert('Por favor, informe o e-mail do destinatário.');
      return;
    }

    setIsSending(true);
    try {
      // Import email service dynamically to avoid SSR issues
      const { sendBillEmail } = await import('@/services/billingService');

      await sendBillEmail({
        to: emailTo,
        subject,
        message,
        transaction,
        client,
        billNumber,
        issueDate,
        companyName,
        companyLogo,
        bankDetails,
        includePDF: sendWithPDF
      });

      alert('Cobrança enviada com sucesso!');
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Erro ao enviar a cobrança. Tente novamente.');
    } finally {
      setIsSending(false);
    }
  };

  // Format currency for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Calculate days until due
  const getDaysUntilDue = () => {
    if (!transaction.due_date) return null;
    const due = new Date(transaction.due_date);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Enviar Cobrança por E-mail
          </DialogTitle>
          <DialogDescription>
            Envie a cobrança da transação para o e-mail do cliente
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email To */}
          <div className="space-y-2">
            <Label htmlFor="email-to">Para</Label>
            <Input
              id="email-to"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="cliente@exemplo.com"
              disabled={isSending}
            />
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Assunto</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSending}
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              disabled={isSending}
            />
          </div>

          {/* Options */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="send-pdf"
                checked={sendWithPDF}
                onChange={(e) => setSendWithPDF(e.target.checked)}
                className="rounded"
                disabled={isSending}
              />
              <Label htmlFor="send-pdf" className="text-sm">
                Anexar cobrança em PDF
              </Label>
            </div>
          </div>

          {/* Transaction Summary */}
          <div className="bg-muted p-3 rounded-lg space-y-1">
            <p className="text-sm font-medium">Resumo da Cobrança:</p>
            <p className="text-xs text-muted-foreground">
              {transaction.description}
            </p>
            <p className="text-sm font-semibold text-terracotta-600">
              {formatCurrency(transaction.amount)}
            </p>
            <p className="text-xs text-muted-foreground">
              Vencimento: {transaction.due_date
                ? new Date(transaction.due_date).toLocaleDateString('pt-BR')
                : 'Não definido'
              }
            </p>
            {daysUntilDue !== null && (
              <p className="text-xs">
                <span className={
                  daysUntilDue < 0
                    ? 'text-red-600 font-medium'
                    : daysUntilDue <= 7
                    ? 'text-yellow-600 font-medium'
                    : 'text-green-600'
                }>
                  {daysUntilDue < 0
                    ? `${Math.abs(daysUntilDue)} dias de atraso`
                    : daysUntilDue === 0
                    ? 'Vence hoje'
                    : `Vence em ${daysUntilDue} dias`
                  }
                </span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              disabled={isSending}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={isSending || !emailTo}
              className="flex-1 flex items-center gap-2"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSending ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}